<script lang="ts">
import { useRouteMeta } from '@/plugins/useRouteMeta'
import { useInstantWinsStore } from '@/stores/instant-wins'
import type { InstantWinDraw } from '@/types'
import {
  PrizeCategories,
  type TIconValue,
  type TPrizeCategoryValue,
  Themes,
} from '@/utils/helper-objects'
import { toValue } from '@/utils/utils'
import { defineComponent } from 'vue'
import type { RouteRecordRaw } from 'vue-router'

import { Social } from './AppSocialIcons.vue'

type NavLink = {
  route: {
    name: string
    hash?: string
    query?: Record<string, string>
  }
  icon: TIconValue
  text: string
}

/**
 * Displays a navigation bottom with menu links.
 *
 * @see https://www.makethingsaccessible.com/guides/site-navigation-is-not-an-aria-menu/
 */
export default defineComponent({
  setup() {
    return {
      instantWinsStore: useInstantWinsStore(),
      routeMeta: useRouteMeta(),
      Social,
      Themes,
    }
  },
  data() {
    return {
      open: true,
      memberMenuIsVisible: false,
    }
  },
  computed: {
    navRoutes(): Array<NavLink> {
      return this.routeMeta
        .flatRoutes((route: RouteRecordRaw) =>
          toValue(route.meta?.nav?.bottom?.display ?? false)
        )
        .map(
          (route: RouteRecordRaw) =>
            ({
              route: {
                name: route.name,
              },
              text: route.meta?.nav?.bottom?.text as string,
              icon: route.meta?.nav?.bottom?.icon,
            }) as NavLink
        )
        .concat([
          ...(this.hasInstantGamesType(PrizeCategories.MysteryBoxes)
            ? [
                {
                  route: {
                    name: 'prizes',
                    query: { tab: 'mystery-boxes', switch: 'instant-wins' },
                  },
                  text: 'nav.bottom.boxes',
                  icon: this.Icons.Present,
                },
              ]
            : []),
          ...(this.hasInstantGamesType(PrizeCategories.InstantPacks)
            ? [
                {
                  route: {
                    name: 'prizes',
                    query: { tab: 'instant-packs', switch: 'instant-wins' },
                  },
                  text: 'nav.bottom.packs',
                  icon: this.Icons.CardPack,
                },
              ]
            : []),
          ...(this.hasInstantGamesType(PrizeCategories.InstantSpins)
            ? [
                {
                  route: {
                    name: 'prizes',
                    query: { tab: 'instant-spins', switch: 'instant-wins' },
                  },
                  text: 'nav.bottom.spins',
                  icon: this.Icons.SlotMachine,
                },
              ]
            : []),
        ])
    },
  },
  mounted() {
    if (
      !this.instantWinsStore.hasLoadedAll &&
      this.instantWinsStore.instantWins.size === 0
    ) {
      void this.instantWinsStore.fetchInstantWins()
    }
  },
  methods: {
    activeLink(routeDetails: NavLink) {
      const checkRouteName =
        this.$router.currentRoute.value.name === routeDetails.route.name

      if (routeDetails.route.hash) {
        return (
          checkRouteName &&
          this.$router.currentRoute.value.hash === routeDetails.route.hash
        )
      }

      if (routeDetails.route.query) {
        for (const [key, value] of Object.entries(routeDetails.route.query)) {
          if (this.$router.currentRoute.value.query[key] !== value) {
            return false
          }
        }

        return checkRouteName
      }

      return checkRouteName
    },
    hasInstantGamesType(type: TPrizeCategoryValue): boolean {
      const instantWinDrawsForNav =
        this.instantWinsStore.futureInstantWinDraws.filter(
          (draw) => draw.is_visible ?? true
        )

      return instantWinDrawsForNav.some((draw: InstantWinDraw) =>
        draw.main_prize.categories?.includes(type)
      )
    },
  },
})
</script>

<template>
  <nav
    v-if="!routeMeta.isGamePlayRoute($router.currentRoute.value.name as string)"
    id="bottomNav"
    ref="bottomNav"
    class="fixed bottom-0 flex h-fit w-full gap-2 overflow-x-auto border-t border-t-slate-200 bg-pure-white md:hidden"
  >
    <ol role="list" class="flex w-full">
      <li v-for="(routeDetails, key) in navRoutes" :key class="flex-1">
        <RouterLink
          :to="routeDetails.route"
          class="flex flex-col items-center justify-center gap-1 whitespace-nowrap p-3"
          :class="{
            'border-t-4 border-t-primary font-bold text-primary':
              activeLink(routeDetails),
            'border-t-4 border-t-transparent': !activeLink(routeDetails),
          }"
          :tabindex="0"
        >
          <AppIcon v-if="routeDetails.icon" :icon="routeDetails.icon" />

          {{ $t(routeDetails.text, { clubName: $theme.displayName }) }}
        </RouterLink>
      </li>
    </ol>
  </nav>
</template>

<style lang="scss" scoped>
nav {
  z-index: 2147483629; // max z-index value
}
</style>
